<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePortalStore } from '@/stores/portal.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useApps } from '@/composables/device'
import { truncateStart } from '@/lib/utils'

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
})

const { t } = getI18n()
const portalStore = usePortalStore()
const planningStore = usePlanningStore()
const { phoneCall, webUrlOpen, emailOpen } = useApps()

const { referenceTypeEnum } = planningStore
const referenceTypeMeta = portalStore.getLocalizedReferenceTypes()
const metaFor = (type) => referenceTypeMeta.find(m => m.id === type)

const ACTION_BY_TYPE = {
  [referenceTypeEnum.PHONE]: phoneCall,
  [referenceTypeEnum.EMAIL]: emailOpen,
  [referenceTypeEnum.SOCIAL]: webUrlOpen,
}
const runAction = (reference) => ACTION_BY_TYPE[reference.reference_type]?.(reference.value)
const chipLabel = (reference) => {
  const value = truncateStart(reference.value, 26)
  return reference.label ? `${reference.label} · ${value}` : value
}

const references = computed(() => props.contact.references ?? [])
const defaultReferences = computed(() => references.value.filter(r => r.is_default))
const otherReferences = computed(() => references.value.filter(r => !r.is_default))
</script>

<template>
  <div class="q-px-md q-py-sm">
    <template v-if="defaultReferences.length">
      <div class="text-caption text-grey-6 text-uppercase text-right q-mb-xs">{{ t('common.default') }}</div>
      <div
        v-for="reference in defaultReferences"
        :key="reference.id"
        class="full-width text-right q-mb-xs"
      >
        <q-chip
          clickable
          size="md"
          class="ref-chip"
          :icon="metaFor(reference.reference_type)?.icon"
          :color="metaFor(reference.reference_type)?.color"
          text-color="white"
          :label="chipLabel(reference)"
          @click="runAction(reference)"
        />
      </div>
      <q-separator v-if="otherReferences.length" class="q-mb-sm" />
    </template>

    <div
      v-for="reference in otherReferences"
      :key="reference.id"
      class="full-width text-right q-mb-xs"
    >
      <q-chip
        clickable
        size="md"
        outline
        class="ref-chip"
        :icon="metaFor(reference.reference_type)?.icon"
        :color="metaFor(reference.reference_type)?.color"
        :label="chipLabel(reference)"
        @click="runAction(reference)"
      />
    </div>
  </div>
</template>

<style scoped>
.ref-chip {
  max-width: 100%;
}
</style>
